import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  PageContainer,
  DataTable,
  TableToolbar,
  StatusChip,
  normalizeList,
  formatCurrency,
  formatDate,
  formatDateTime,
} from "components/shared";
import {
  useGetDashboardQuery,
  useGetSalesQuery,
  useGetOrdersQuery,
  useGetProductsQuery,
  useGetInventoryAnalyticsQuery,
} from "state/api";

// =============================================================================
// Unified Reports page — replaces the former Sales / Order / Product report
// pages (the product one used hardcoded mock data, now removed). A single
// PageContainer with a TableToolbar tab bar switching between the three report
// types, all wired to real analytics endpoints.
// =============================================================================

const REPORT_TABS = [
  { label: "Sales", value: "sales" },
  { label: "Orders", value: "orders" },
  { label: "Products", value: "products" },
];

const toISODate = (d) => d.toISOString().split("T")[0];

// A small, theme-aware KPI card (no hardcoded colors).
const KpiCard = ({ title, value, accent = "text.primary" }) => {
  const theme = useTheme();
  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: "12px", height: "100%", bgcolor: theme.palette.background.alt }}
    >
      <CardContent>
        <Typography variant="body2" color="text.secondary" noWrap>
          {title}
        </Typography>
        <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, color: accent }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
};

const ChartCard = ({ title, children }) => (
  <Card variant="outlined" sx={{ borderRadius: "12px", mb: 3 }}>
    <CardContent>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

const Spinner = () => (
  <Box display="flex" justifyContent="center" py={8}>
    <CircularProgress />
  </Box>
);

// ── Sales tab ────────────────────────────────────────────────────────────────
const SalesReport = ({ queryDates }) => {
  const theme = useTheme();
  const { data: salesRes, isFetching } = useGetSalesQuery(queryDates);
  const { data: dashRes } = useGetDashboardQuery();

  const sales = salesRes?.data || {};
  const stats = dashRes?.data || {};

  const dailyChartData = useMemo(
    () =>
      (sales.salesByDay || []).map((d) => ({
        date: d._id
          ? new Date(d._id).toLocaleDateString("en-IN", {
              month: "short",
              day: "numeric",
            })
          : "—",
        revenue: d.totalRevenue || 0,
        orders: d.orderCount || 0,
      })),
    [sales.salesByDay]
  );

  const topProducts = useMemo(
    () =>
      (sales.topProducts || []).map((p, i) => ({
        _id: p.product?._id || i,
        name: p.product?.name || "Unknown",
        qty: p.totalQuantity || 0,
        revenue: p.totalRevenue || 0,
      })),
    [sales.topProducts]
  );

  const topProductColumns = [
    { field: "rank", headerName: "#", width: 60, render: (_r, i) => i + 1 },
    { field: "name", headerName: "Product" },
    { field: "qty", headerName: "Qty Sold", align: "right" },
    {
      field: "revenue",
      headerName: "Revenue",
      align: "right",
      render: (r) => formatCurrency(r.revenue),
    },
  ];

  const chartColor = theme.palette.secondary.main;
  const barColor = theme.palette.success.main;

  if (isFetching) return <Spinner />;

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Total Revenue"
            value={formatCurrency(sales.totalRevenue)}
            accent="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard title="Total Orders" value={sales.totalOrders || 0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Avg Order Value"
            value={formatCurrency(sales.averageOrderValue)}
            accent="info.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Month Revenue"
            value={formatCurrency(stats.monthRevenue)}
            accent="secondary.main"
          />
        </Grid>
      </Grid>

      <ChartCard title="Revenue Over Time">
        {dailyChartData.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            No sales in the selected range
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={dailyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Legend />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke={chartColor}
                name="Revenue"
                dot={false}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="Orders Per Day">
        {dailyChartData.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            No orders in the selected range
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dailyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="orders" fill={barColor} name="Orders" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
        Top Products
      </Typography>
      <DataTable
        columns={topProductColumns}
        rows={topProducts}
        emptyText="No product sales in the selected range"
      />
    </>
  );
};

// ── Orders tab ───────────────────────────────────────────────────────────────
const ORDER_STATUS_KEYS = [
  { key: "pending", label: "Pending", accent: "warning.main" },
  { key: "confirmed", label: "Confirmed", accent: "info.main" },
  { key: "processing", label: "Processing", accent: "primary.main" },
  { key: "shipped", label: "Shipped", accent: "secondary.main" },
  { key: "delivered", label: "Delivered", accent: "success.main" },
  { key: "cancelled", label: "Cancelled", accent: "error.main" },
];

const OrdersReport = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { data: dashRes } = useGetDashboardQuery();
  const { data, isFetching } = useGetOrdersQuery({
    page: page + 1,
    limit: rowsPerPage,
  });

  const obs = dashRes?.data?.ordersByStatus || {};

  const { rows, total } = useMemo(
    () => normalizeList(data, { page: page + 1, pageSize: rowsPerPage }),
    [data, page, rowsPerPage]
  );

  const columns = [
    {
      field: "invoiceNumber",
      headerName: "Invoice",
      render: (o) => o.invoiceNumber || o._id?.slice(-6).toUpperCase(),
    },
    {
      field: "createdAt",
      headerName: "Date",
      render: (o) => formatDateTime(o.createdAt),
    },
    {
      field: "customer",
      headerName: "Customer",
      render: (o) => o.customer?.name || "Walk-in",
    },
    {
      field: "total",
      headerName: "Total",
      align: "right",
      render: (o) => formatCurrency(o.total),
    },
    {
      field: "paymentStatus",
      headerName: "Paid",
      render: (o) => <StatusChip status={o.paymentStatus} />,
    },
    {
      field: "status",
      headerName: "Status",
      render: (o) => <StatusChip status={o.status} />,
    },
  ];

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {ORDER_STATUS_KEYS.map((s) => (
          <Grid item xs={6} sm={4} md={2} key={s.key}>
            <KpiCard title={s.label} value={obs[s.key] || 0} accent={s.accent} />
          </Grid>
        ))}
      </Grid>

      <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
        Recent Orders
      </Typography>
      <DataTable
        columns={columns}
        rows={rows}
        loading={isFetching}
        emptyText="No orders found"
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
      />
    </>
  );
};

// ── Products tab (replaces the mock productreports) ──────────────────────────
const stockStatus = (p) => {
  const stock = p.stock ?? 0;
  const min = p.minStock ?? 10;
  if (stock === 0) return "cancelled"; // out of stock -> red via palette
  if (stock <= min) return "pending"; // low stock -> amber
  return "delivered"; // in stock -> green
};
const stockLabel = (p) => {
  const stock = p.stock ?? 0;
  const min = p.minStock ?? 10;
  if (stock === 0) return "Out of stock";
  if (stock <= min) return "Low stock";
  return "In stock";
};

const ProductsReport = () => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { data: invRes } = useGetInventoryAnalyticsQuery();
  const { data, isFetching } = useGetProductsQuery({
    page: page + 1,
    limit: rowsPerPage,
    search,
  });

  const inv = invRes?.data || {};

  const { rows, total } = useMemo(
    () => normalizeList(data, { page: page + 1, pageSize: rowsPerPage }),
    [data, page, rowsPerPage]
  );

  const columns = [
    { field: "name", headerName: "Product" },
    {
      field: "productCode",
      headerName: "Code",
      render: (p) => p.productCode || "—",
    },
    {
      field: "sellingPrice",
      headerName: "Price",
      align: "right",
      render: (p) => formatCurrency(p.sellingPrice),
    },
    { field: "stock", headerName: "Stock", align: "right", render: (p) => p.stock ?? 0 },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      render: (p) => formatDate(p.updatedAt),
    },
    {
      field: "status",
      headerName: "Status",
      render: (p) => <StatusChip status={stockStatus(p)} label={stockLabel(p)} />,
    },
  ];

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <KpiCard title="Total Products" value={inv.totalProducts || 0} />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <KpiCard
            title="Total Stock Units"
            value={Number(inv.totalStock || 0).toLocaleString("en-IN")}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <KpiCard
            title="Low Stock"
            value={inv.lowStockProducts || 0}
            accent="warning.main"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <KpiCard
            title="Out of Stock"
            value={inv.outOfStockProducts || 0}
            accent="error.main"
          />
        </Grid>
      </Grid>

      <TableToolbar
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        searchPlaceholder="Search products…"
      />
      <DataTable
        columns={columns}
        rows={rows}
        loading={isFetching}
        emptyText="No products found"
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
      />
    </>
  );
};

// ── Page shell ───────────────────────────────────────────────────────────────
const ReportsPage = () => {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const [tab, setTab] = useState("sales");
  const [startDate, setStartDate] = useState(toISODate(thirtyDaysAgo));
  const [endDate, setEndDate] = useState(toISODate(today));
  const [queryDates, setQueryDates] = useState({
    startDate: thirtyDaysAgo.toISOString(),
    endDate: today.toISOString(),
  });

  const applyDates = () => {
    setQueryDates({
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString(),
    });
  };

  return (
    <PageContainer
      title="Reports"
      subtitle="Sales, orders and product analytics in one place"
      actions={
        tab === "sales" && (
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              label="From"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="contained" onClick={applyDates}>
              Apply
            </Button>
          </Box>
        )
      }
    >
      <TableToolbar tabs={REPORT_TABS} tabValue={tab} onTabChange={setTab} />

      {tab === "sales" && <SalesReport queryDates={queryDates} />}
      {tab === "orders" && <OrdersReport />}
      {tab === "products" && <ProductsReport />}
    </PageContainer>
  );
};

export default ReportsPage;
